<script setup>
import { usersService } from '@/services/usersService.js';
import { authOperationsService } from '@/services/authOperationsService.js';
import { ref, onMounted, onUnmounted, computed, defineAsyncComponent } from 'vue';
import { useLayout, useTheme } from '@/layout/composables/layout';
import websocketService from '@/services/websocketService.js';
import { useAuthState } from '@/composables/authState.js';
import { useWebSocket } from '@/composables/useWebSocket';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications.js';
import { useDesktopStartupTask } from '@/composables/useDesktopStartup.js';
import AppMenuItem from './AppMenuItem.vue';
import AccountDropdown from './AccountDropdown.vue';
import DesktopUpdateButton from '@/components/desktop/DesktopUpdateButton.vue';
const DesktopNotificationNav = defineAsyncComponent(() => import('@/components/desktop/DesktopNotificationNav.vue'));
import { usePageVisibilitySnapshot } from '@/composables/usePageVisibilitySnapshot.js';
import {
    clearUserContext,
    loadUserAvatar,
    loadUserContext,
    setUserAvatar,
    useUserContext
} from '@/composables/userContext.js';
import { clearMyBotsCache } from '@/utils/myBotsCache.js';
import { createDefaultPageVisibility, pageVisibilityPages, creationCapabilities } from '@/config/pageVisibility.js';

const { toggleSidebarCollapse, layoutState } = useLayout();
const { isDarkTheme, toggleTheme } = useTheme();
const { setLoggingOut, setStatus } = useAuthState();
const { subscribe } = useWebSocket();
const router = useRouter();
const userMenu = ref(null);
const userButton = ref(null);
const sidebarMenu = ref(null);

const usersLoadError = ref('');
const showSwitchUserModal = ref(false);
const switchUserSearchQuery = ref('');
const availableUsers = ref([]);
const isLoadingUsers = ref(false);
const { userContext, userAvatar } = useUserContext();
const username = computed(() => userContext.value?.username || 'User');
const userId = computed(() => userContext.value?.user_id || 'Unknown');
const isOwner = computed(() => Boolean(userContext.value?.is_owner));
const switchedByAdmin = computed(() => Boolean(userContext.value?.switched_by_admin));

const { operationSuccess } = useNotifications();

const finishDesktopStartup = useDesktopStartupTask();
const isCollapsed = computed(() => layoutState.sidebarCollapsed);

const desktopApp = Boolean(window.__CORECRY_DESKTOP__);
const model = ref(pageVisibilityPages.map((page) => ({
    label: page.label, icon: page.icon, to: page.path, pageKey: page.key, adminControlled: true
})));

const defaultPageVisibilitySettings = Object.freeze(createDefaultPageVisibility());

const PAGE_VISIBILITY_STORAGE_KEY = 'corecry_page_visibility_settings';

const cloneVisibilitySettings = (settings) => JSON.parse(JSON.stringify(settings));

const pageVisibilitySettings = ref(cloneVisibilitySettings(defaultPageVisibilitySettings));

const pageVisibilityLoaded = ref(false);

let unsubscribePageVisibilityWs = null;

const applyPageVisibilitySettings = (settings, markLoaded = true) => {
    const mergedSettings = cloneVisibilitySettings(defaultPageVisibilitySettings);

    if (settings && typeof settings === 'object') {
        for (const key in settings) {
            if (Object.prototype.hasOwnProperty.call(mergedSettings, key)) {
                mergedSettings[key] = { ...mergedSettings[key], ...settings[key] };
            }
        }
    }

    pageVisibilitySettings.value = mergedSettings;

    if (markLoaded) {
        pageVisibilityLoaded.value = true;
    }
};

const loadCachedPageVisibilitySettings = () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const cachedSettings = localStorage.getItem(PAGE_VISIBILITY_STORAGE_KEY);
        if (cachedSettings) {
            const parsedSettings = JSON.parse(cachedSettings);
            applyPageVisibilitySettings(parsedSettings, true);
        }
    } catch (error) {
        globalThis.clientLogger?.error('Failed to load cached page visibility settings:', error);
    }
};

const cachePageVisibilitySettings = (settings) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const settingsToCache = settings ?? pageVisibilitySettings.value;
        localStorage.setItem(
            PAGE_VISIBILITY_STORAGE_KEY,
            JSON.stringify(settingsToCache)
        );
    } catch (error) {
        globalThis.clientLogger?.error('Failed to cache page visibility settings:', error);
    }
};

loadCachedPageVisibilitySettings();

const loadPageVisibilitySettings = usePageVisibilitySnapshot('sidebar', data => {
    applyPageVisibilitySettings(data, true);
    cachePageVisibilitySettings(pageVisibilitySettings.value);
}, { onFailure: () => applyPageVisibilitySettings(pageVisibilitySettings.value, true) });

// Check if a page should be visible to current user
// Using computed to ensure Vue tracks reactivity properly
const isPageVisible = computed(() => {
    return (pageKey) => {
        if (!pageVisibilityLoaded.value) {
            return false;
        }

        const pageSettings = pageVisibilitySettings.value[pageKey];
        if (!pageSettings) {
            return false;
        }

        const { visible_to_all, visible_to_admin_only } = pageSettings;

        if (visible_to_all) {
            return true;
        }

        if (visible_to_admin_only) {
            return isOwner.value;
        }

        return false;
    };
});

onMounted(async () => {
    const hasCachedUser = Boolean(userContext.value?.user_id);
    try {
        // Render cached identity immediately and revalidate it against the session.
        await loadUserContext({ force: hasCachedUser });
        setStatus('authenticated');
    } catch (error) {
        globalThis.clientLogger?.error('Failed to load user data:', error);
        if (!hasCachedUser) {
            setStatus('logged-out');
        }
    }

    await Promise.all([
        loadUserAvatar({ force: Boolean(userAvatar.value) }).catch((error) => {
            globalThis.clientLogger?.error('Failed to load user avatar:', error);
        }),
        loadPageVisibilitySettings()
    ]).finally(finishDesktopStartup);

    // Listen for avatar updates
    window.addEventListener('avatar-updated', handleAvatarUpdate);

    // Listen for page visibility updates
    window.addEventListener('page-visibility-updated', handlePageVisibilityUpdate);

    if (!unsubscribePageVisibilityWs) {
        unsubscribePageVisibilityWs = subscribe('page_visibility_updated', async () => {
            await loadPageVisibilitySettings();
            cachePageVisibilitySettings(pageVisibilitySettings.value);
        });
    }

    // Add click outside listener
    document.addEventListener('click', handleClickOutside);

});

// Handle avatar updates
const handleAvatarUpdate = (event) => {
    setUserAvatar(event.detail.avatar);
};

// Handle page visibility updates
const handlePageVisibilityUpdate = async () => {
    await loadPageVisibilitySettings();
};

// Cleanup event listeners
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('avatar-updated', handleAvatarUpdate);
    window.removeEventListener('page-visibility-updated', handlePageVisibilityUpdate);
    if (unsubscribePageVisibilityWs) {
        unsubscribePageVisibilityWs();
        unsubscribePageVisibilityWs = null;
    }
});

// Logout functionality
const handleLogout = async (event) => {
    // Prevent event bubbling
    if (event) {
        event.stopPropagation();
    }
    try {
        // Clear user data stored in the browser
        // Clear any cached data
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('cached_')) {
                localStorage.removeItem(key);
            }
        });
        clearMyBotsCache();
        clearUserContext();
        
        // Set logging out flag to prevent router redirects
        setLoggingOut(true);
        
        await authOperationsService.logout({
            method: 'POST',
            credentials: 'include'
        });

        websocketService.disconnect();
        setStatus('logged-out');

        // Use window.location for reliable redirect
        window.location.href = '/auth/login';
    } catch (error) {
        globalThis.clientLogger?.error('Logout error:', error);
        // Force redirect even on error
        websocketService.disconnect();
        setStatus('logged-out');
        window.location.href = '/auth/login';
    }
};

const toggleUserMenu = (event) => {
    // Prevent event bubbling to avoid sidebar collapse on mobile
    if (event) {
        event.stopPropagation();
    }
    userMenu.value = !userMenu.value;
};

// Switch user modal functions
const openSwitchUserModal = () => {
    userMenu.value = false; // Close user menu
    showSwitchUserModal.value = true;
    loadAvailableUsers();
};

const closeSwitchUserModal = () => {
    showSwitchUserModal.value = false;
    switchUserSearchQuery.value = '';
};

const loadAvailableUsers = async () => {
    isLoadingUsers.value = true;
    usersLoadError.value = '';
    try {
        let currentUserId = null;
        try {
            const userData = await loadUserContext();
            currentUserId = userData.user_id;
            setStatus('authenticated');
        } catch (error) {
            globalThis.clientLogger?.error('Failed to get current user info:', error);
            setStatus('logged-out');
        }

        const data = await usersService.adminList({ credentials: 'include' });
            const users = data.users.filter(user =>
                user.status === 'registered' && user.user_id !== currentUserId
            );

            // Load avatars for each user
            await Promise.all(users.map(async (user) => {
                try {
                    const avatarData = await usersService.adminAvatar(user.user_id, { credentials: 'include' });
                    user.avatar = avatarData.avatar || null;
                } catch (error) {
                    globalThis.clientLogger?.error(`Failed to load avatar for user ${user.user_id}:`, error);
                    user.avatar = null;
                }
            }));

        availableUsers.value = users;
    } catch (error) {
        availableUsers.value = [];
        usersLoadError.value = 'Unable to load users. Close this window and try again.';
        globalThis.clientLogger?.error('Failed to load users:', error);
    } finally {
        isLoadingUsers.value = false;
    }
};

// Switch to selected user
const switchToUser = async (user) => {
    try {
        const response = await authOperationsService.switchUser({
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ user_id: user.user_id })
        });

        if (response) {
            operationSuccess(`Switched to user: ${response.username}`);
            closeSwitchUserModal();

            clearMyBotsCache();
            clearUserContext();

            window.location.reload();
        }
    } catch (error) {
        globalThis.clientLogger?.error('Failed to switch user:', error);
        // You could add error notification here if needed
    }
};

// Switch back to admin
const switchBackToAdmin = async () => {
    try {
        const response = await authOperationsService.switchBack({
            method: 'POST',
            credentials: 'include'
        });

        if (response) {
            operationSuccess(`Switched back to admin: ${response.username}`);

            clearMyBotsCache();
            clearUserContext();

            window.location.reload();
        }
    } catch (error) {
        globalThis.clientLogger?.error('Failed to switch back:', error);
    }
};


// Handle avatar loading errors
const handleAvatarError = (user) => {
    globalThis.clientLogger?.warn(`Failed to load avatar for user ${user.user_id}, falling back to placeholder`);
    // Remove the broken avatar so placeholder shows
    user.avatar = null;
};

// Filtered users for switch user modal
const filteredSwitchUsers = computed(() => {
    if (!switchUserSearchQuery.value.trim()) {
        return availableUsers.value;
    }
    return availableUsers.value.filter(user =>
        user.username.toLowerCase().includes(switchUserSearchQuery.value.toLowerCase()) ||
        user.user_id.toLowerCase().includes(switchUserSearchQuery.value.toLowerCase())
    );
});

// Helper functions for avatars
const getInitial = (username) => {
    return username ? username.charAt(0).toUpperCase() : '?';
};

const getAvatarStyle = () => {
    return {
        backgroundColor: '#6B7280',
        color: 'white'
    };
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    const userMenuElement = document.querySelector('.user-menu');
    const userButtonElement = document.querySelector('.user-button');

    if (userMenu.value &&
        userMenuElement &&
        userButtonElement &&
        !userMenuElement.contains(event.target) &&
        !userButtonElement.contains(event.target)) {
        userMenu.value = false;
    }
};

// Create new bot functionality
const createNewBot = async () => {
    try {
        // Close sidebar on mobile after navigation
        if (window.innerWidth <= 768) {
            layoutState.staticMenuMobileActive = false;
            layoutState.overlayMenuActive = false;
        }
        await router.push('/create-bot');
    } catch (error) {
        globalThis.clientLogger?.error('Navigation error:', error);
        // Fallback: force navigation if router.push fails
        window.location.href = '/create-bot';
    }
};

const handleSidebarWheel = (event) => {
    event.stopPropagation()
    
    // Get the sidebar menu element (the scrollable part)
    const menu = sidebarMenu.value
    if (!menu) {
        event.preventDefault()
        return
    }
    
    const delta = event.deltaY
    const scrollTop = menu.scrollTop
    const scrollHeight = menu.scrollHeight
    const clientHeight = menu.clientHeight
    
    // Check if we're at the boundaries
    const isAtTop = scrollTop <= 0
    const isAtBottom = scrollTop + clientHeight >= scrollHeight
    
    // If scrolling down at bottom, or scrolling up at top, prevent default
    if ((delta > 0 && isAtBottom) || (delta < 0 && isAtTop)) {
        event.preventDefault()
    }
};
defineExpose({ handleLogout, openSwitchUserModal, switchBackToAdmin, pageVisibilitySettings, pageVisibilityLoaded });
</script>

<template>
    <div class="layout-sidebar-new" :class="{ 'sidebar-collapsed': isCollapsed }" @wheel="handleSidebarWheel">
        <!-- Right border separator for collapsed state -->
        <div class="sidebar-right-separator"></div>
        
        <!-- Top Section with Logo and Controls -->
        <div class="sidebar-top">
            <div class="logo-section">
                <!-- Universal logo element -->
                <div 
                    class="logo-container"
                    :class="{ 'logo-clickable': isCollapsed }"
                    @click="isCollapsed ? toggleSidebarCollapse() : $router.push('/')"
                >
                    <img src="/logo.svg" alt="logo" class="logo-themed" />
                </div>
            </div>
            
            <div class="top-controls" :class="{ 'controls-visible': !isCollapsed }">
                <!-- Owner status and collapse controls -->
                <Transition name="controls-fade">
                    <div v-if="!isCollapsed" class="controls-row">
                        <button type="button" class="sidebar-theme-toggle control-item" @click="toggleTheme"
                            :aria-label="isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'"
                            :title="isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'">
                            <i :class="['pi', isDarkTheme ? 'pi-sun' : 'pi-moon']" aria-hidden="true"></i>
                        </button>

                        <!-- Collapse button - for all users -->
                        <button
                            type="button"
                            class="collapse-toggle sidebar-hover"
                            @click="toggleSidebarCollapse"
                        >
                            <i class="pi pi-angle-left"></i>
                        </button>
                    </div>
                </Transition>
            </div>
        </div>

        <div class="sidebar-menu" ref="sidebarMenu">
            <ul class="layout-menu">
                <template v-for="(item, i) in model" :key="item">
                    <app-menu-item v-if="!item.separator && (!item.adminControlled || isPageVisible(item.pageKey))" :item="item" :index="i" :root="false"></app-menu-item>
                    <li v-if="item.separator" class="menu-separator"></li>
                    <DesktopNotificationNav v-if="desktopApp && item.pageKey === 'statistics'" />
                </template>
            </ul>
        </div>

        <DesktopUpdateButton :collapsed="isCollapsed" />
        <div v-if="pageVisibilityLoaded && creationCapabilities(pageVisibilitySettings, isOwner).available" class="create-bot-section">
            <button class="create-bot-btn" @click="createNewBot">
                <Transition name="icon-fade">
                    <i v-if="isCollapsed" class="pi pi-plus create-bot-icon"></i>
                </Transition>
                <Transition name="text-fade" appear>
                    <div v-if="!isCollapsed" class="button-content">
                        <span class="create-bot-text">Create New Bot</span>
                    </div>
                </Transition>
            </button>
        </div>

        <!-- Separator between menu and user -->
        <div class="menu-user-separator"></div>

        <!-- User Section -->
        <div class="sidebar-user">
            <button ref="userButton" class="user-button" aria-haspopup="menu" :aria-expanded="Boolean(userMenu)" @click="toggleUserMenu($event)">
                <div class="user-avatar">
                    <img
                        v-if="userAvatar"
                        :src="userAvatar"
                        alt="Profile picture"
                        class="user-avatar-img"
                    />
                    <i v-else class="pi pi-user user-icon"></i>
                </div>
                <div class="user-text">
                    <span class="user-name">{{ username }}</span>
                    <span class="user-id">{{ userId }}</span>
                </div>
            </button>
            
            <AccountDropdown v-if="userMenu" :username="username" :user-id="userId" :avatar="userAvatar"
                :is-owner="isOwner" :switched-by-admin="switchedByAdmin"
                @switch-user="openSwitchUserModal" @switch-back="switchBackToAdmin" @logout="handleLogout"
                @close="userMenu = false; userButton?.focus()" />
        </div>
    </div>

    <!-- Switch User Modal -->
    <div v-if="showSwitchUserModal" class="modal-overlay" @pointerdown.self="closeSwitchUserModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h3>Switch User</h3>
                <button class="close-btn" @click="closeSwitchUserModal">
                    <i class="pi pi-times"></i>
                </button>
            </div>
            <div class="modal-body">
                <!-- Search input -->
                <div class="add-chat-search">
                    <div class="search-input-container">
                        <i class="pi pi-search search-icon"></i>
                        <input
                            id="switch-user-search"
                            type="text"
                            v-model="switchUserSearchQuery"
                            placeholder="Search users..."
                            class="search-input"
                        />
                    </div>
                </div>
                <div class="users-list-container">
                    <div v-if="isLoadingUsers" class="loading-users">
                        <i class="pi pi-spin pi-spinner"></i>
                        Loading users...
                    </div>
                    <div v-else-if="usersLoadError" class="no-users" role="alert">{{ usersLoadError }}</div>
                    <div v-else-if="filteredSwitchUsers.length === 0" class="no-users">
                        {{ switchUserSearchQuery ? 'No users match your search' : 'No users available' }}
                    </div>
                    <div v-else class="users-list">
                        <div
                            v-for="user in filteredSwitchUsers"
                            :key="user.user_id"
                            class="user-item"
                            @click="switchToUser(user)"
                        >
                            <div class="user-avatar">
                                <img
                                    v-if="user.avatar"
                                    :src="user.avatar"
                                    alt="Profile picture"
                                    class="user-avatar-img"
                                    @error="handleAvatarError(user)"
                                />
                                <div v-else class="avatar-placeholder" :style="getAvatarStyle(user.username)">
                                    {{ getInitial(user.username) }}
                                </div>
                            </div>
                            <div class="user-info">
                                <div class="user-name">{{ user.username }}</div>
                                <div class="user-id">ID: {{ user.user_id }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Modal Actions -->
                <div class="modal-actions">
                    <div class="action-buttons">
                        <button type="button" class="btn-cancel" @click="closeSwitchUserModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
/* Dark theme variables */
:root {
    --sidebar-hover-bg: var(--theme-sidebar-hover, #303030);
    --button-hover-bg: var(--theme-hover, rgba(255, 255, 255, 0.1));
}

.layout-sidebar-new {
    position: fixed;
    left: 0;
    top: 0;
    width: 240px;
    /* Use CSS custom property for dynamic viewport height */
    height: var(--vh, 100vh);
    height: 100dvh;
    background-color: var(--theme-section, #343434);
    display: flex;
    flex-direction: column;
    z-index: 999;
    /* Include transform transition so mobile slide-in animates smoothly */
    transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    box-sizing: border-box;
    border: none;
    overflow-x: hidden; /* Disable horizontal scroll */
    overscroll-behavior: contain; /* Prevent scroll propagation */
    padding: 0;
}

/* Collapsed Sidebar Styles */
.layout-sidebar-new.sidebar-collapsed {
    width: 60px;
    /* Keep transform transition here too for consistent behavior on mobile */
    transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    background-color: var(--theme-section, #343434);
    box-sizing: border-box;
    border: none;
    padding: 0;
    overflow: hidden;
}

/* Right separator for collapsed sidebar */
.sidebar-right-separator {
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background-color: var(--theme-border, #333436);
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    z-index: 1;
}

.sidebar-collapsed .sidebar-right-separator {
    opacity: 1;
}

/* Keep logo in the same position */
.sidebar-collapsed .sidebar-top {
    justify-content: space-between;
    padding: 1rem 1rem;
}

.sidebar-collapsed .logo-section {
    margin-top: -0.45rem;
    margin-left: 0.125rem;
}

.sidebar-collapsed .top-controls {
    opacity: 0;
    transition-delay: 0s; /* No delay when collapsing */
}

/* Remove icon right margins in collapsed state */
.layout-sidebar-new.sidebar-collapsed .layout-menu .layout-menuitem-icon {
    margin-right: 0 !important;
}

/* Collapsed sidebar styles */
.sidebar-collapsed .menu-user-separator {
    width: 100%;
    margin: 0.5rem 0 0 0;
    flex-shrink: 0;
    box-sizing: border-box;
}

/* In collapsed state, DO NOT change avatar positioning - only hide text */
.sidebar-collapsed .user-text {
    opacity: 0;
    max-width: 0;
    transition: opacity 0.3s ease, max-width 0.3s ease;
}

/* User avatar position is already fixed by base styles */

.sidebar-collapsed .sidebar-menu {
    padding: 0;
    margin-top: 0.5rem;
    /* Ensure consistent height */
    min-height: auto;
}

/* Menu items keep same position when collapsed - no overrides needed */

/* User avatar stays in normal flow - no positioning changes */

/* Top Section */
.sidebar-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1rem;
    position: relative;
    height: 4rem;
    box-sizing: border-box;
}

.logo-section {
    display: flex;
    align-items: center;
    margin-top: -0.45rem;
    margin-left: 0.125rem;
    flex-shrink: 0;
    width: 5rem;
    height: 5rem;
}

.top-controls {
    display: flex;
    align-items: center;
    margin-left: auto;
    opacity: 0;
    transition: opacity 0.2s ease;
    transition-delay: 0s;
}

.top-controls.controls-visible {
    opacity: 1;
    transition-delay: 0.3s; /* Delay matches sidebar animation duration */
}

.controls-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.control-item {
    display: flex;
    align-items: center;
}

.controls-fade-enter-active {
    transition: opacity 0.2s ease 0.3s;
}

.controls-fade-leave-active {
    transition: opacity 0.2s ease;
}

.controls-fade-enter-from,
.controls-fade-leave-to {
    opacity: 0;
}

.logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    transition: width 0.3s ease, height 0.3s ease, border-radius 0.3s ease;
    width: 2rem;
    height: 2rem;
}

.logo-container img {
    width: 5rem;
    height: 5rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin-top: 0.35rem;
}

/* Logo color - white for dark theme */
.logo-themed {
    filter: var(--theme-logo-filter, invert(1) brightness(1)) !important;
}

/* Logo clickable state styles can be added here if needed */



.collapse-toggle {
    background: none;
    border: none;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
    color: var(--theme-text, #ffffff);
    cursor: pointer;
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.collapse-toggle i {
    font-size: 1.75rem;
}

.collapse-toggle:hover {
    background-color: var(--theme-interaction-hover, rgba(255, 255, 255, 0.1)) !important;
}

/* Navigation Menu */
.sidebar-menu {
    flex: 1;
    padding: 0;
    margin-top: 0.5rem;
    overflow-y: auto;
    overflow-x: hidden; /* Disable horizontal scroll */
    overscroll-behavior: contain; /* Prevent scroll propagation */
}

/* Menu styles are defined in _menu.scss */

.create-bot-section {
    padding: 0.5rem 0.75rem;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-start;
    transition: padding 0.3s ease;
}

.create-bot-btn {
    position: relative;
    width: 100%;
    height: 39px;
    background: var(--theme-sidebar-create-bg, #ffffff) !important;
    border: 1px solid var(--theme-control, #4E4E4E);
    border-radius: 19.5px; /* Half of height (39px / 2 = 19.5px) */
    cursor: pointer;
    color: var(--theme-sidebar-create-text, #0D0D0D) !important;
    font-weight: 500;
    font-size: 1rem;
    transition: width 0.3s ease, border-radius 0.3s ease;
    box-sizing: border-box;
    
    .button-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
    }
    
    .create-bot-icon {
        font-size: 0.875rem;
        color: var(--theme-sidebar-create-text, #0D0D0D) !important;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
    
    .create-bot-text {
        color: var(--theme-sidebar-create-text, #0D0D0D) !important;
        text-align: center;
        font-size: 1.125rem; /* Increased text size */
        font-weight: 600;
    }
}

.create-bot-btn:hover, .create-bot-btn:active {
    background: var(--theme-sidebar-create-hover, #f8f9fa); --theme-sidebar-create-bg: var(--theme-sidebar-create-hover);
}



/* Collapsed state styles */
.sidebar-collapsed .create-bot-section {
    padding: 0.5rem 0.75rem;
}

.sidebar-collapsed .create-bot-btn {
    width: 39px;
    border-radius: 19.5px;
    transition: width 0.3s ease, border-radius 0.3s ease;


}

.menu-user-separator {
    width: 100%;
    height: 1px;
    background-color: var(--theme-profile-separator, #505155);
    margin: 0.5rem 0 0 0;
    flex-shrink: 0;
    /* Ensure consistent positioning */
    box-sizing: border-box;
}

/* User Section */
.sidebar-user {
    padding: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    /* Fixed height for stability */
    height: 60px;
    position: relative; /* Added for dropdown positioning */
}

.user-button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.25rem 0.75rem;
    margin: 0;
    width: 100%;
    height: 100%;
    background: none;
    border: none;
    border-radius: 0;
    cursor: pointer;
    color: var(--theme-text, #ffffff);
    text-align: left;
    transition: background-color 0.2s ease;
    box-sizing: border-box;
}

.user-button:hover {
    background-color: color-mix(in srgb, var(--theme-section, #343434) 85%, #000);
}

.user-avatar {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.125rem; /* Align with Create New Bot button center */
}

.user-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

.user-icon {
    font-size: 2rem;
    color: var(--theme-text, #ffffff);
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.user-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    max-width: 200px;
    opacity: 1;
    transition: opacity 0.2s ease, max-width 0.2s ease;
}

.user-name {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.2;
    color: inherit;
}

.user-id {
    font-size: 0.75rem;
    color: var(--theme-muted, #B4B4B4) !important;
    line-height: 1.2;
}

/* Dark theme styles for create bot button - keep it white */
.app-dark .create-bot-btn {
    background: var(--theme-accent, #ffffff) !important;
    color: var(--theme-on-accent, #0D0D0D) !important;
    border-color: var(--theme-control, #4E4E4E);
    
    i {
        color: var(--theme-on-accent, #0D0D0D) !important;
    }
    
    span {
        color: var(--theme-on-accent, #0D0D0D) !important;
    }
    
    &:hover {
        background: var(--theme-accent-hover, #f8f9fa) !important;
        color: var(--theme-on-accent, #0D0D0D) !important;

        i, span {
            color: var(--theme-on-accent, #0D0D0D) !important;
        }
    }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
    transition: opacity 0.2s ease;
}

.icon-fade-enter-from,
.icon-fade-leave-to {
    opacity: 0;
}

/* Text transition with delay */
.text-fade-enter-active {
    transition: opacity 0.3s ease 0.3s; /* 0.3s delay after sidebar animation */
}

.text-fade-leave-active {
    transition: opacity 0.2s ease;
}

.text-fade-enter-from,
.text-fade-leave-to {
    opacity: 0;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--theme-overlay, rgba(0, 0, 0, 0.5));
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding-left: 240px; /* Default: expanded sidebar */
    transition: padding-left 0.3s ease-in-out; /* Smooth transition */
}

/* Adjust modal positioning when sidebar is collapsed */
.layout-wrapper.sidebar-collapsed .modal-overlay {
    padding-left: 64px; /* Collapsed sidebar width */
}

/* Tablet and mobile: fix modal positioning */
@media (max-width: 991px) {
    .modal-overlay {
        padding-top: 0 !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .modal-content {
        margin-top: 0 !important;
        position: relative !important;
        top: 0 !important;
    }
}

/* Mobile: no sidebar offset */
@media (max-width: 768px) {
    .modal-overlay {
        padding-left: 0 !important;
    }
}

.modal-content {
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 1rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
}

/* Dark theme for popup */
.modal-content {
    background: var(--theme-surface, #2F2F2F);
    border: 1px solid var(--theme-border, #3B3B3B);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    border-bottom: 1px solid var(--surface-border);

    h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--text-color);
    }
}

.close-btn {
    background: none;
    border: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s ease;

    &:hover {
        background: var(--theme-interaction-hover, rgba(255, 255, 255, 0.1));
        color: var(--text-color);
    }

    i {
        font-size: 1.25rem;
    }
}

.modal-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

/* Search input styles */
.add-chat-search {
    .search-input-container {
        position: relative;

        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--theme-muted, #B4B4B4);
            font-size: 1rem;
        }

        .search-input {
            width: 100%;
            padding: 0.75rem 0.75rem 0.75rem 2.5rem;
            border: 1px solid var(--surface-border);
            border-radius: 0.5rem;
            background: var(--surface-section);
            color: var(--text-color);
            font-size: 0.9rem;
            height: 38px;

            &:focus {
                outline: none;
                border-color: #3B85F7;
                box-shadow: 0 0 0 2px rgba(59, 133, 247, 0.1);
            }

            &::placeholder {
                color: var(--theme-muted, #B4B4B4);
            }
        }
    }
}

/* Users list styles */
.users-list-container {
    max-height: 300px;
    overflow-y: auto;

    .loading-users {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 2rem;
        color: var(--text-color-secondary);

        i {
            font-size: 1.25rem;
        }
    }

    .no-users {
        text-align: center;
        padding: 2rem;
        color: var(--text-color-secondary);
    }

    .users-list {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .user-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem;
            border-radius: 0.5rem;
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
                background: var(--theme-interaction-hover, var(--surface-hover));
            }

            .user-avatar {
                width: 32px;
                height: 32px;
                flex-shrink: 0;

                img {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .avatar-placeholder {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    background: var(--theme-badge-neutral, #6B7280);
                    color: var(--theme-muted, white);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 0.8rem;
                }
            }

            .user-info {
                flex: 1;
                min-width: 0;

                .user-name {
                    font-weight: 500;
                    color: var(--text-color);
                    margin-bottom: 0.125rem;
                    font-size: 0.9rem;
                }

                .user-id {
                    font-size: 0.8rem;
                    color: var(--text-color-secondary);
                }
            }
        }
    }
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;

    .action-buttons {
        display: flex;
        gap: 0.5rem;
    }

    .btn-cancel {
        padding: 0.5rem 1rem;
        border: 1px solid var(--surface-border);
        border-radius: 0.5rem;
        background: var(--surface-section);
        color: var(--text-color);
        cursor: pointer;
        font-size: 0.9rem;
        transition: all 0.2s ease;

        &:hover {
            background: var(--theme-interaction-hover, var(--surface-hover));
        }
    }
}

/* Theme control occupies the former connection indicator slot. */
.sidebar-theme-toggle {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 3px 4px;
    border-radius: 10px;
    font-size: 10px;
    height: 21px;
    min-height: 21px;
    max-height: 21px;
    width: 38px;
    min-width: 38px;
    box-sizing: border-box;
    justify-content: center;
    cursor: pointer;
    color: var(--text-color);
    font-weight: 500;
    transition: all 0.3s ease;
    background: var(--theme-selected, rgba(255, 255, 255, 0.05));
    border: 1px solid var(--theme-control, rgba(255, 255, 255, 0.1));

    &:hover { background: var(--theme-interaction-pressed, var(--surface-hover)); }
    &:focus-visible { outline: 2px solid var(--text-color); outline-offset: 2px; }
    i { font-size: 12px; }
}

/* ToggleSwitch styles moved to global styles.scss */
</style>
